import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { Badge, Button, Card, Dialog, Drawer, Select, TextAreaField, TextField, useToast } from '@/components/play'
import { useFeedbackActions } from '@/hooks/useFeedback'
import { captureFeedbackContext, feedbackKindBadgeTone, feedbackKindLabels } from '@/lib/feedback'
import type { CreateFeedbackItemPayload, FeedbackItemResource, FeedbackKind } from '@/types/feedback'
import { api } from '@/util/api'

interface FeedbackCreateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (item: FeedbackItemResource) => void
}

// What captureFeedbackContext adds to an item, said once in both layouts.
const CONTEXT_NOTE = 'We’ll include the page you’re on and your browser’s details.'

const createableKinds: FeedbackKind[] = ['bug', 'feedback', 'feature_request']
const kindItems = createableKinds.map((option) => ({ value: option, label: feedbackKindLabels[option] }))

export function FeedbackCreateModal({ open, onOpenChange, onCreated }: FeedbackCreateModalProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { createFeedbackItem } = useFeedbackActions()

  const [isMobile, setIsMobile] = useState(false)
  const [kind, setKind] = useState<FeedbackKind>('feedback')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [similarItems, setSimilarItems] = useState<FeedbackItemResource[]>([])
  // Missing fields say so beside themselves, as Play's Form does, and the first takes focus.
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({})
  const titleInput = useRef<HTMLInputElement>(null)
  const descriptionInput = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!open || title.trim().length < 4) {
      setSimilarItems([])
      return
    }

    const timer = window.setTimeout(async () => {
      try {
        const response = await fetchSimilar(title)
        setSimilarItems(response)
      } catch {
        setSimilarItems([])
      }
    }, 250)

    return () => window.clearTimeout(timer)
  }, [open, title])

  const inferredContext = useMemo(() => captureFeedbackContext(location.pathname), [location.pathname])
  const inferredEventId = typeof inferredContext.route_params === 'object' ? (inferredContext.route_params as any)?.event_id : null
  const inferredStreamId = typeof inferredContext.route_params === 'object' ? (inferredContext.route_params as any)?.stream_id : null

  async function fetchSimilar(query: string) {
    const response = await api.listFeedbackItems({ query, sort: 'most_voted' })
    return response.data.slice(0, 3)
  }

  function resetForm() {
    setKind('feedback')
    setTitle('')
    setDescription('')
    setSimilarItems([])
    setErrors({})
  }

  async function submit(status: 'draft' | 'open') {
    const missing = {
      title: title.trim() ? undefined : 'Add a title.',
      description: description.trim() ? undefined : 'Add a description.',
    }
    if (missing.title || missing.description) {
      setErrors(missing)
      ;(missing.title ? titleInput.current : descriptionInput.current)?.focus()
      return
    }

    setSubmitting(true)
    try {
      const payload: CreateFeedbackItemPayload = {
        kind,
        status,
        title: title.trim(),
        description_markdown: description.trim(),
        event_id: inferredEventId,
        stream_id: inferredStreamId,
        context: inferredContext,
      }
      const response = await createFeedbackItem(payload)
      resetForm()
      onOpenChange(false)
      onCreated?.(response.data)
      navigate(`/feedback/${response.data.id}`)
      toast({
        title: status === 'draft' ? 'Saved as draft' : 'Feedback submitted',
        description: response.data.attributes.title,
        tone: 'success',
      })
    } catch (err: any) {
      toast({
        title: 'Feedback not sent',
        description: err?.response?.data?.error?.message || 'Check your connection and try again.',
        tone: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const content = (
    <div className="space-y-5">
      <Select
        label="Type"
        items={kindItems}
        value={kind}
        onValueChange={(value) => {
          if (value) setKind(value)
        }}
        placeholder="Choose a type"
      />

      <TextField
        ref={titleInput}
        id="feedback-title"
        label="Title"
        value={title}
        onChange={(event) => {
          setTitle(event.target.value)
          setErrors((current) => ({ ...current, title: undefined }))
        }}
        error={errors.title}
        placeholder="What happened or what should we build?"
      />

      <TextAreaField
        ref={descriptionInput}
        id="feedback-description"
        label="Description"
        value={description}
        onChange={(event) => {
          setDescription(event.target.value)
          setErrors((current) => ({ ...current, description: undefined }))
        }}
        error={errors.description}
        placeholder="Add steps, context, and the outcome you expected."
      />

      {similarItems.length > 0 && (
        <Card title="Similar feedback" icon={<Sparkles />} headingLevel={3}>
          <div className="space-y-2">
            {similarItems.map((item) => (
              <Link
                key={item.id}
                to={`/feedback/${item.id}`}
                onClick={() => onOpenChange(false)}
                className="flex min-h-11 items-start justify-between gap-3 rounded-lg bg-neutral-1 px-3 py-2 transition-colors hover:bg-neutral-3"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-10">{item.attributes.title}</p>
                  <p className="mt-1 text-xs text-neutral-7">
                    {item.attributes.votes_count} votes · {item.attributes.comments_count} comments
                  </p>
                </div>
                <Badge tone={feedbackKindBadgeTone(item.attributes.kind)}>{feedbackKindLabels[item.attributes.kind]}</Badge>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  )

  const actions = (
    <>
      <Button variant="secondary" onClick={() => submit('draft')} disabled={submitting}>
        Save draft
      </Button>
      <Button onClick={() => submit('open')} loading={submitting}>
        Submit
      </Button>
    </>
  )

  if (isMobile) {
    return (
      <Drawer
        side="bottom"
        open={open}
        onOpenChange={onOpenChange}
        title="Share feedback"
        description={CONTEXT_NOTE}
        footer={actions}
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Dialog
      size="wide"
      open={open}
      onOpenChange={onOpenChange}
      title="Share feedback"
      description={CONTEXT_NOTE}
      footer={actions}
    >
      {content}
    </Dialog>
  )
}
